import React, { useCallback, useMemo, useState } from 'react';
import {
  Pressable,
  View,
  FlatList,
  ActivityIndicator,
} from 'react-native';
import { heightPercentageToDP as hp } from 'react-native-responsive-screen';
import { Feather } from '@expo/vector-icons';

import LoadingOverlay from '../components/LoadingOverlay';
import CustomText from '../components/CustomText';
import CustomTextElipses from '../components/CustomTextElipses';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Util from '../util/util.js';
import webStyles from '../style/webStyles.js';
import { usePreviousReports, hasComments, useResponsiveStyles, useViewedReports } from '../hooks';
import styles from '../style/screens/previousReportStyles';
import AppStyles from '../AppStyles';
import { useTranslation } from '../i18n';

const PreviousReport = (props) => {
  const { t } = useTranslation('reports');

  const { organization, admin, userAdmin } = props.route.params;
  const defaultOrganization = organization;

  // Use responsive styles hook for platform detection
  const { isWeb, containerStyle, screenWidth } = useResponsiveStyles();

  // Adjust fonts for viewing the web portal on smaller devices
  const scale = isWeb && screenWidth < 800 ? 0.7 : 1;

  // Use the previous reports hook with pagination
  const { reports, reportsLoading, isLoadingMore, hasMore, refresh, loadMore } = usePreviousReports(organization);

  // Viewed/unread tracking + "Unread only" filter (default: show all)
  const { isViewed, markViewedLocal } = useViewedReports();
  const [unreadOnly, setUnreadOnly] = useState(false);
  const displayedReports = useMemo(
    () => (unreadOnly ? reports.filter((r) => !isViewed(r.uuid)) : reports),
    [reports, unreadOnly, isViewed]
  );

  // Render individual report item - V3 styled
  const renderReportItem = useCallback(({ item: element, index }) => (
    <Pressable
      style={({ pressed }) => [
        { cursor: 'pointer' },
        pressed && { opacity: 0.7 },
      ]}
      onPress={() => {
        element.index = displayedReports.length - index;
        element.defaultOrganization = defaultOrganization;
        markViewedLocal(element.uuid);
        props.navigation.navigate('ReportDetails', element);
      }}
    >
      <View style={webStyles.reportContainer}>
        {/* Status icon */}
        <View style={webStyles.reportCheck}>
          {!isViewed(element.uuid) && (
            <View
              accessibilityLabel={t('previous.unreadReportLabel')}
              style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: AppStyles.colorSet.brandPrimary, marginVertical: 3 }}
            />
          )}
          {element.isUrgent && !hasComments(element) && !element.isClosed && (
            <Feather name={'alert-circle'} size={20 * scale} color={AppStyles.colorSet.error} />
          )}
          {hasComments(element) && !element.isClosed && (
            <Feather
              name={'message-circle'}
              size={20 * scale}
              color={AppStyles.colorSet.brandPrimary}
            />
          )}
          {element.isClosed && (
            <Feather name={'check-circle'} size={20 * scale} color={AppStyles.colorSet.success} />
          )}
        </View>
        {/* Report content */}
        <View style={webStyles.reportInfoContainer}>
          <View style={webStyles.reportInfo}>
            <CustomText style={webStyles.reportInfoItem} font="regular" size={13} color={AppStyles.colorSet.textSecondary}>
              {t('previous.idValue', { id: Util.formatUuid(element.uuid) })}
            </CustomText>
            <CustomText style={webStyles.reportInfoItem} font="regular" size={13} color={AppStyles.colorSet.textSecondary}>
              {Util.timeConverter(element.timestamp)}
            </CustomText>
          </View>
          <CustomTextElipses
            numberOfLines={2}
            ellipsizeMode="tail"
            style={webStyles.report}
            font={isViewed(element.uuid) ? 'medium' : 'semiBold'}
            size={16 * scale}
            color={AppStyles.colorSet.textPrimary}
          >
            {Util.formatDescription(element.content)}
          </CustomTextElipses>
        </View>
        {/* Chevron */}
        <Feather name="chevron-right" size={20} color={AppStyles.colorSet.textMuted} />
      </View>
    </Pressable>
  ), [displayedReports.length, defaultOrganization, scale, props.navigation, isViewed, markViewedLocal]);

  // Render loading footer for pagination
  const renderFooter = useCallback(() => {
    if (!isLoadingMore) return null;
    return (
      <View style={styles.loadingMore}>
        <ActivityIndicator size="small" color={AppStyles.colorSet.brandPrimary} />
        <CustomText font="regular" size={14} color={AppStyles.colorSet.textSecondary} style={{ marginTop: 8 }}>
          {t('previous.loadingMore')}
        </CustomText>
      </View>
    );
  }, [isLoadingMore]);

  // Render empty state
  const renderEmpty = useCallback(() => {
    if (reportsLoading) return null;
    const showingUnreadEmpty = unreadOnly && reports.length > 0;
    return (
      <View style={styles.emptyState}>
        <Feather name={showingUnreadEmpty ? 'check-circle' : 'inbox'} size={48} color={AppStyles.colorSet.textMuted} />
        <CustomText
          font="medium"
          size={18}
          color={AppStyles.colorSet.textSecondary}
          style={{ marginTop: 16, textAlign: 'center' }}
        >
          {showingUnreadEmpty ? t('previous.caughtUpTitle') : t('previous.emptyTitle')}
        </CustomText>
        <CustomText
          font="regular"
          size={14}
          color={AppStyles.colorSet.textMuted}
          style={{ marginTop: 8, textAlign: 'center' }}
        >
          {showingUnreadEmpty ? t('previous.caughtUpSubtitle') : t('previous.emptySubtitle')}
        </CustomText>
      </View>
    );
  }, [reportsLoading, unreadOnly, reports.length]);

  return (
    <View style={containerStyle}>
      {/* Global header */}
      <Header organization={organization} admin={admin} userAdmin={userAdmin} />

      <View style={{ flex: 1, backgroundColor: AppStyles.colorSet.whiteColor }}>
        {/* Title section - V3 style */}
        <View style={styles.titleContainer}>
          <CustomText
            style={styles.title}
            font="semiBold"
            size={isWeb ? 28 : 24}
            color={AppStyles.colorSet.textPrimary}
          >
            {t('previous.title')}
          </CustomText>

          <Pressable
            style={({ pressed }) => [
              styles.refreshButton,
              pressed && { opacity: 0.7 },
            ]}
            onPress={refresh}
          >
            <Feather name="refresh-cw" size={22 * scale} color={AppStyles.colorSet.textSecondary} />
          </Pressable>
        </View>

        {/* Unread-only filter toggle (default: show all) */}
        <View style={{ flexDirection: 'row', paddingHorizontal: 16, paddingBottom: 8 }}>
          <Pressable
            style={({ hovered, focused, pressed }) => [
              {
                flexDirection: 'row',
                alignItems: 'center',
                paddingVertical: 6,
                paddingHorizontal: 10,
                borderRadius: 8,
                borderWidth: 1,
                borderColor: unreadOnly ? '#B45309' : AppStyles.colorSet.textMuted,
              },
              unreadOnly && { backgroundColor: `${AppStyles.colorSet.brandPrimary}25` },
              (hovered || focused || pressed) && { opacity: 0.8 },
              isWeb && { cursor: 'pointer' },
            ]}
            onPress={() => setUnreadOnly(!unreadOnly)}
            accessibilityRole="switch"
            accessibilityState={{ checked: unreadOnly }}
            accessibilityLabel={t('previous.unreadOnlyLabel')}
          >
            <Feather
              name={unreadOnly ? 'check-square' : 'square'}
              size={16}
              color={unreadOnly ? '#B45309' : AppStyles.colorSet.textSecondary}
            />
            <CustomText
              font="medium"
              size={12}
              color={unreadOnly ? '#B45309' : AppStyles.colorSet.textSecondary}
              style={{ marginLeft: 6 }}
            >
              {t('previous.unreadOnly')}
            </CustomText>
          </Pressable>
        </View>

        {/* Report list area */}
        <View style={styles.listContainer}>
          {reportsLoading && <LoadingOverlay message={t('previous.loadingReports')} />}
          <FlatList
            style={{ paddingTop: hp(1), width: '100%' }}
            contentContainerStyle={{ paddingBottom: hp(2) }}
            data={displayedReports}
            extraData={isViewed}
            keyExtractor={(item) => item.uuid}
            renderItem={renderReportItem}
            ListFooterComponent={renderFooter}
            ListEmptyComponent={renderEmpty}
            onEndReached={loadMore}
            onEndReachedThreshold={0.5}
            initialNumToRender={10}
            maxToRenderPerBatch={10}
            windowSize={5}
          />
        </View>
      </View>

      <Footer admin={admin} organization={defaultOrganization} userAdmin={userAdmin} />
    </View>
  );
};

export default PreviousReport;
